import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

/**
 * Drive State Management
 * Manages Company Root → User Root → Files flow
 */
interface DriveState {
  // Company Root
  companyRootId: string | null;
  companyRootLoading: boolean;
  companyRootError: string | null;

  // User Root
  userRootId: string | null;
  userRootLoading: boolean;
  userRootError: string | null;

  // Files
  files: any[];
  filesLoading: boolean;
  filesError: string | null;

  // Flow orchestration
  flowStatus: "idle" | "initializing" | "ready" | "error";
  flowError: string | null;
}

const initialState: DriveState = {
  companyRootId: localStorage.getItem("companyRootId") || null,
  companyRootLoading: false,
  companyRootError: null,

  userRootId: localStorage.getItem("userRootId") || null,
  userRootLoading: false,
  userRootError: null,

  files: [],
  filesLoading: false,
  filesError: null,

  flowStatus: "idle",
  flowError: null,
};

const driveSlice = createSlice({
  name: "drive",
  initialState,
  reducers: {
    // ==================== FLOW ORCHESTRATOR ====================
    /**
     * Initiates the complete drive initialization flow:
     * 1. Check/Create Company Root
     * 2. Check/Create User Root
     * 3. Load User Root Files
     */
    initDriveFlowRequest: (
      state,
      _action: PayloadAction<{
        companyId: string;
        orgId: string;
        companyName: string;
        companyEmail: string;
        userId: string;
      }>
    ) => {
      state.flowStatus = "initializing";
      state.flowError = null;
    },

    initDriveFlowSuccess: (state) => {
      state.flowStatus = "ready";
      state.flowError = null;
    },

    initDriveFlowFailure: (state, action: PayloadAction<string>) => {
      state.flowStatus = "error";
      state.flowError = action.payload;
    },

    // ==================== COMPANY ROOT ====================
    /**
     * Check if company root exists
     * GET /Drive/company/{companyId}/type/company/org/{orgId}
     */
    checkCompanyRootRequest: (
      state,
      _action: PayloadAction<{ companyId: string; orgId: string }>
    ) => {
      state.companyRootLoading = true;
      state.companyRootError = null;
    },

    checkCompanyRootSuccess: (
      state,
      action: PayloadAction<{ companyRootId: string }>
    ) => {
      state.companyRootId = action.payload.companyRootId;
      state.companyRootLoading = false;
      state.companyRootError = null;
      localStorage.setItem("companyRootId", action.payload.companyRootId);
    },

    checkCompanyRootFailure: (state, action: PayloadAction<string>) => {
      state.companyRootLoading = false;
      state.companyRootError = action.payload;
    },

    /**
     * Create company root folder
     * POST /Drive/createFolderRootProject
     */
    createCompanyRootRequest: (
      state,
      _action: PayloadAction<{
        orgId: string;
        companyName: string;
        companyEmail: string;
        companyId: string;
      }>
    ) => {
      state.companyRootLoading = true;
      state.companyRootError = null;
    },

    createCompanyRootSuccess: (
      state,
      action: PayloadAction<{ companyRootId: string }>
    ) => {
      state.companyRootId = action.payload.companyRootId;
      state.companyRootLoading = false;
      state.companyRootError = null;
      localStorage.setItem("companyRootId", action.payload.companyRootId);
    },

    createCompanyRootFailure: (state, action: PayloadAction<string>) => {
      state.companyRootLoading = false;
      state.companyRootError = action.payload;
    },

    // ==================== USER ROOT ====================
    /**
     * Check if user root exists
     * getFolderRootIdByProjectId(userId)
     */
    checkUserRootRequest: (
      state,
      _action: PayloadAction<{ userId: string; companyRootId: string }>
    ) => {
      state.userRootLoading = true;
      state.userRootError = null;
    },

    checkUserRootSuccess: (
      state,
      action: PayloadAction<{ userRootId: string }>
    ) => {
      state.userRootId = action.payload.userRootId;
      state.userRootLoading = false;
      state.userRootError = null;
    },

    checkUserRootFailure: (state, action: PayloadAction<string>) => {
      state.userRootLoading = false;
      state.userRootError = action.payload;
    },

    /**
     * Create user root folder
     * POST /Drive/createFolderRootProject with parentId = companyRootId
     */
    createUserRootRequest: (
      state,
      _action: PayloadAction<{
        userId: string;
        companyRootId: string;
        companyId: string;
      }>
    ) => {
      state.userRootLoading = true;
      state.userRootError = null;
    },

    createUserRootSuccess: (
      state,
      action: PayloadAction<{ userRootId: string }>
    ) => {
      state.userRootId = action.payload.userRootId;
      state.userRootLoading = false;
      state.userRootError = null;
    },

    createUserRootFailure: (state, action: PayloadAction<string>) => {
      state.userRootLoading = false;
      state.userRootError = action.payload;
    },

    // ==================== FILES ====================
    /**
     * Load files from user root folder
     * getFilesByFolderRoot(userRootId)
     */
    loadUserRootFilesRequest: (
      state,
      _action: PayloadAction<{ userRootId: string }>
    ) => {
      state.filesLoading = true;
      state.filesError = null;
    },

    loadUserRootFilesSuccess: (state, action: PayloadAction<{ files: any[] }>) => {
      state.files = action.payload.files;
      state.filesLoading = false;
      state.filesError = null;
    },

    loadUserRootFilesFailure: (state, action: PayloadAction<string>) => {
      state.filesLoading = false;
      state.filesError = action.payload;
    },

    // ==================== RESET ====================
    resetDriveState: () => initialState,
  },
});

export const driveActions = driveSlice.actions;
export const driveReducer = driveSlice.reducer;
